import { useState } from 'react';

import { Radio, Text, TextArea } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

type RejectionModalProps = {
    isOpen: boolean;
    onBack: () => void;
    onSubmit: (reason?: string, detail?: string) => void;
    onSkip: () => void;
};

export const RejectionModal = ({
    isOpen,
    onBack,
    onSubmit,
    onSkip
}: RejectionModalProps) => {
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [detailText, setDetailText] = useState<string>('');

    const handleSubmit = () => {
        onSubmit(selectedReason || undefined, detailText || undefined);
    };

    if (!isOpen) return null;

    return (
        <div className="p-l">
            <button
                onClick={onBack}
                className="mb-m flex items-center gap-xs text-s text-structure-40 hover:text-structure-20"
            >
                <span>&lt;</span>
                <FormattedMessage
                    id="_.credit_card_waitlist.rejection.back"
                    defaultMessage="Back"
                />
            </button>

            <Text className="mb-m text-center text-xl font-bold">
                <FormattedMessage
                    id="_.credit_card_waitlist.rejection.title"
                    defaultMessage="Mind telling us why you're not interested?"
                />
            </Text>

            <Text className="mb-m text-center text-s text-structure-40">
                <FormattedMessage
                    id="_.credit_card_waitlist.rejection.subtitle"
                    defaultMessage="Your feedback helps us improve."
                />
            </Text>

            <div className="mb-m space-y-s">
                <Radio.Group name="rejection-reason">
                    <div className="space-y-s">
                        <Radio.Root
                            variant="frame"
                            className="items-center"
                            id="reason-already-have"
                        >
                            <Radio.Input
                                value="already_have_business_card"
                                checked={
                                    selectedReason ===
                                    'already_have_business_card'
                                }
                                onChange={(e) =>
                                    setSelectedReason(e.target.value)
                                }
                            />
                            <Radio.Label>
                                <Text className="text-s">
                                    <FormattedMessage
                                        id="_.credit_card_waitlist.rejection.reason_already_have"
                                        defaultMessage="I already have a business credit card"
                                    />
                                </Text>
                            </Radio.Label>
                        </Radio.Root>

                        <Radio.Root
                            variant="frame"
                            className="items-center"
                            id="reason-dont-need"
                        >
                            <Radio.Input
                                value="dont_need_credit_card"
                                checked={
                                    selectedReason === 'dont_need_credit_card'
                                }
                                onChange={(e) =>
                                    setSelectedReason(e.target.value)
                                }
                            />
                            <Radio.Label>
                                <Text className="text-s">
                                    <FormattedMessage
                                        id="_.credit_card_waitlist.rejection.reason_dont_need"
                                        defaultMessage="I don't need a credit card"
                                    />
                                </Text>
                            </Radio.Label>
                        </Radio.Root>

                        <Radio.Root
                            variant="frame"
                            className="items-center"
                            id="reason-benefits"
                        >
                            <Radio.Input
                                value="benefits_not_compelling"
                                checked={
                                    selectedReason === 'benefits_not_compelling'
                                }
                                onChange={(e) =>
                                    setSelectedReason(e.target.value)
                                }
                            />
                            <Radio.Label>
                                <Text className="text-s">
                                    <FormattedMessage
                                        id="_.credit_card_waitlist.rejection.reason_benefits"
                                        defaultMessage="The benefits aren't compelling enough"
                                    />
                                </Text>
                            </Radio.Label>
                        </Radio.Root>

                        <Radio.Root
                            variant="frame"
                            className="items-center"
                            id="reason-prefer-flexi"
                        >
                            <Radio.Input
                                value="prefer_flexi_loan"
                                checked={selectedReason === 'prefer_flexi_loan'}
                                onChange={(e) =>
                                    setSelectedReason(e.target.value)
                                }
                            />
                            <Radio.Label>
                                <Text className="text-s">
                                    <FormattedMessage
                                        id="_.credit_card_waitlist.rejection.reason_prefer_flexi"
                                        defaultMessage="I prefer to keep my borrowing with my Flexi-Loan"
                                    />
                                </Text>
                            </Radio.Label>
                        </Radio.Root>

                        <Radio.Root
                            variant="frame"
                            className="items-center"
                            id="reason-other"
                        >
                            <Radio.Input
                                value="other"
                                checked={selectedReason === 'other'}
                                onChange={(e) =>
                                    setSelectedReason(e.target.value)
                                }
                            />
                            <Radio.Label>
                                <Text className="text-s">
                                    <FormattedMessage
                                        id="_.credit_card_waitlist.rejection.reason_other"
                                        defaultMessage="Other"
                                    />
                                </Text>
                            </Radio.Label>
                        </Radio.Root>
                    </div>
                </Radio.Group>

                <TextArea
                    value={detailText}
                    onChange={(e) => setDetailText(e.target.value)}
                    placeholder="Tell us more (optional)"
                    rows={3}
                />
            </div>

            <div className="flex gap-s">
                <button
                    onClick={onSkip}
                    className="flex-1 rounded-full border-2 bg-white px-l py-m text-m font-med transition-colors hover:bg-structure-95"
                    style={{ borderColor: '#07172b', color: '#07172b' }}
                >
                    <FormattedMessage
                        id="_.credit_card_waitlist.rejection.skip"
                        defaultMessage="Skip to dashboard"
                    />
                </button>
                <button
                    onClick={handleSubmit}
                    className="flex-1 rounded-full px-l py-m text-m font-med text-white transition-colors"
                    style={{ backgroundColor: '#07172b' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0a1f3a';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#07172b';
                    }}
                >
                    <FormattedMessage
                        id="_.credit_card_waitlist.rejection.submit"
                        defaultMessage="Submit"
                    />
                </button>
            </div>
        </div>
    );
};
